import {
  SanityAtButton,
  SanityAtImage,
  SanityBuildingCard,
  SanityCustomCard,
  SanityMlSiteBanner,
  SanityOrBaseContent,
  SanityOrCarousel,
  SanityOrCarouselType,
  SanityOrContainer,
  SanityOrContainerBlock,
  SanityOrContainerTwoCol,
  SanityOrMap,
  SanityTmFlexible,
} from '../../services/sanity/types'
import { OrContainerBlock, OrContainerProps, OrContainerTwoColProps, TmFlexibleProps } from '.'
import {
  AtImageProps,
  AtLinkButtonProps,
  MlSiteBannerProps,
  OrBaseContentProps,
  OrContentBlockAProps,
  OrContentBlockCProps,
  OrContentBlockFProps,
  OrMapProps,
  OrMapTileProps,
  OrTextBlockProps,
  OrCardCarouselProps,
  contentItems,
  MlBuildingCardProps,
  Image,
  OrContentBlockBProps,
  OrContentBlockGProps,
  OrContentBlockEProps,
  OrContentBlockDProps,
  AtIconInformativeProps,
  MlCustomCardProps,
  CarouselType,
  FormWithTitleAndTypeProps,
  Media,
} from '@studio/atomic-ui'
import { MlCtaBarProps, Image as DefaultImageProps } from '@studio/atomic-ui'
import { SanityMlCtaBarProps, SanitySmTextBlock, SanityOrForm } from '../../services/sanity/types'
import {
  mapButton,
  mapFormField,
  mapLink,
  mapSpaceCard,
  mapSnackbar,
  mapNotification,
} from '../../services/sanity/mappers'
import { getSelectedVideoVariant } from '../../utils/ab-testing'
import { PortableTextProps } from '@portabletext/react'
import { baseUrl, googleMapsApiKey } from '../../env'
import { Building } from '../../types/building'
import { addBasePath } from '../../utils/helpers'
import { mapImage } from '../../services/sanity/client'
import { addAnalyticsFieldToForm } from '../../utils/google-analytics-form-field'
import { DropdownOption } from '@studio/atomic-ui'
import { ImageUrlBuilderOptionsWithAliases } from '@sanity/image-url/lib/types/types'

/**
 * Map Sanity TmFlex Object to TmFlexible props
 * @param SanityTmFlexible TmFlex object from Sanity
 * @param title The title of the Template in Sanity
 * @returns Clean object of type TmFlexibleProps to use in <TmFlexible />
 */
export const mapTmFlexibleProps = (
  { blocks = [], hideTitle, id }: SanityTmFlexible,
  title = '',
  buildings: Building[]
): TmFlexibleProps => {
  const hasBlocks = Boolean(blocks && blocks.length > 0)
  return {
    id: id || '',
    title,
    hideTitle: hideTitle ?? false,
    blocks: hasBlocks ? mapTmFlexibleBlocksProps(blocks, buildings) : [],
  }
}

/**
 * Map Sanity TmFlex Blocks Array to an Array of Atomic Lib components
 * @param sanityTmFlexibleBlocks Array of block from Sanity TmFlex
 * @returns Array of Atomic Lib Components (Options: OrContainerProps)
 */
const mapTmFlexibleBlocksProps = (
  sanityTmFlexibleBlocks: SanityTmFlexible['blocks'],
  buildings: Building[]
): TmFlexibleProps['blocks'] => {
  const tmFlexibleBlocks = [] as TmFlexibleProps['blocks']

  for (const sanityBlock of sanityTmFlexibleBlocks) {
    const blockType = sanityBlock?.type

    if (blockType === 'orContainer')
      tmFlexibleBlocks.push({ type: 'or-container', blocks: mapSanityOrContainerToOrContainer(sanityBlock, buildings) })
    if (blockType === 'orContainerTwoCol')
      tmFlexibleBlocks.push({
        type: 'or-container-two-col',
        blocks: mapSanityOrContainerTwoColToProps(sanityBlock as SanityOrContainerTwoCol, buildings),
      })
    if (blockType === 'orBaseContent')
      tmFlexibleBlocks.push({ type: 'or-base-content', blocks: mapSanityOrBaseContentToOrBaseContent(sanityBlock) })
  }

  return tmFlexibleBlocks.filter(Boolean)
}

/**
 * Map a Sanity OrContainer to Atomic Lib OrContainer
 * @param sanityOrContainer
 * @returns Clean object of type OrContainerProps
 */
const mapSanityOrContainerToOrContainer = (
  sanityOrContainer: SanityOrContainer,
  buildings: Building[]
): OrContainerProps => {
  const orContainer = {} as OrContainerProps

  orContainer.containerTitle = sanityOrContainer?.containerTitle || ''
  orContainer.description = sanityOrContainer?.description || ''
  orContainer.blocks = mapSanityOrContainerBlockToOrContainerBlock(sanityOrContainer?.blocks, buildings)

  return orContainer
}

/**
 * Map a Sanity OrContainerTwoCol to Atomic Lib OrContainerTwoCol
 * @param sanityOrContainerTwoCol
 * @returns Clean object of type OrContainerTwoColProps
 */
const mapSanityOrContainerTwoColToProps = (
  sanityOrContainerTwoCol: SanityOrContainerTwoCol,
  buildings: Building[]
): OrContainerTwoColProps => {
  return {
    columnRatio: sanityOrContainerTwoCol.columnRatio,
    blocks: mapSanityOrContainerBlockToOrContainerBlock(sanityOrContainerTwoCol?.blocks, buildings),
  }
}

/**
 * Transform a Sanity OrContainer Blocks to Atomic Lib OrContainer Blocks
 * @param block
 * @returns an object ready to use in a component of the Atomic Lib
 */
export const mapSanityOrContainerBlockToOrContainerBlock = (
  sanityOrContainerBlocks: Array<SanityOrContainerBlock>,
  buildings: Building[]
): Array<OrContainerBlock> => {
  const orContainerBlocks = new Array<OrContainerBlock>()

  for (const block of sanityOrContainerBlocks) {
    const blockType = block?.type

    if (blockType === 'textBlock')
      orContainerBlocks.push({ type: 'or-text-block', fields: sanityTextBlockToOrTextBlock(block) })
    if (blockType === 'ctaBar') orContainerBlocks.push({ type: 'ml-cta-bar', fields: sanityCtaBarToMlCtaBar(block) })
    if (blockType === 'form') orContainerBlocks.push({ type: 'form', fields: sanityFormToForm(block) })
    if (blockType === 'carousel')
      orContainerBlocks.push({ type: 'or-card-carousel', fields: sanityCarouselToCarousel(block, buildings) })
    if (blockType === 'mlSiteBanner')
      orContainerBlocks.push({ type: 'ml-site-banner', fields: sanityMlSiteBannerToMlSiteBanner(block, buildings) })
    if (blockType === 'orMap') orContainerBlocks.push({ type: 'or-map', fields: sanityOrMapToOrMap(block) })
    if (blockType === 'orBaseContent')
      orContainerBlocks.push({ type: 'or-base-content', fields: mapSanityOrBaseContentToOrBaseContent(block) })
    if (blockType === 'orSpaces') orContainerBlocks.push({ type: 'or-spaces', fields: block })
  }

  return orContainerBlocks
}

/**
 * Map a Sanity SmTextBlock Object to Atomic Lib OrTextBlock props
 * @param sanityTextBlock
 * @returns OrTextBlockContentProps Object
 */
const sanityTextBlockToOrTextBlock = (sanityTextBlock: SanitySmTextBlock): OrTextBlockProps['content'] => {
  let orTextBlock: OrTextBlockProps['content'] = []

  orTextBlock = sanityTextBlock?.content || []

  return orTextBlock
}

/**
 * Map a Sanity MLCtaBar Object to Atomic Lib MlCtaBar props
 * @param sanityCtaBar
 * @returns MlCtaBarProps Object
 */
const sanityCtaBarToMlCtaBar = (sanityCtaBar: SanityMlCtaBarProps): MlCtaBarProps => {
  const mlCtaBar = {} as MlCtaBarProps

  mlCtaBar.primaryLabel = sanityCtaBar?.label || ''
  mlCtaBar.primaryIcon = sanityCtaBar?.rightIcon?.src || ''
  mlCtaBar.primaryActionValue = sanityCtaBar?.actionValue || ''
  mlCtaBar.primaryTarget = sanityCtaBar?.target || '_self'

  if (sanityCtaBar.secondaryLabel) {
    mlCtaBar.secondaryLabel = sanityCtaBar.secondaryLabel
    mlCtaBar.secondaryIcon = sanityCtaBar.secondaryRightIcon?.src || ''
    mlCtaBar.secondaryActionValue = sanityCtaBar.secondaryActionValue || ''
    mlCtaBar.secondaryTarget = sanityCtaBar.secondaryTarget || '_self'
  }

  return mlCtaBar
}

/**
 * Map a Sanity OrForm Object to Atomic Lib Form props
 * @param sanityFormProps
 * @returns FormProps Object
 */
const sanityFormToForm = (sanityFormProps: SanityOrForm): FormWithTitleAndTypeProps => {
  const form = {} as FormWithTitleAndTypeProps

  form.fields = [
    ...addAnalyticsFieldToForm(sanityFormProps.fields.map((f) => mapFormField(f, { insideFlexTemplate: true }))),
    {
      type: 'submit',
      label: sanityFormProps.submitButton?.label ?? '',
    },
  ]
  form.title = sanityFormProps.title
  form.submissionType = sanityFormProps.submissionType
  if (sanityFormProps.formId) {
    form.formId = sanityFormProps.formId
  }
  if (sanityFormProps.isGate) {
    form.isGate = sanityFormProps.isGate
    form.gateRevealsContentAfter = sanityFormProps.gateRevealsContentAfter
    form.gateAttachedFileUrl = sanityFormProps.gateAttachedFileUrl
    form.sendGatedContentByEmail = sanityFormProps.sendGatedContentByEmail
  }
  if (sanityFormProps.successSnackbar) {
    form.successSnackbar = mapSnackbar(sanityFormProps.successSnackbar)
  }
  if (sanityFormProps.serverErrorNotification) {
    form.serverErrorNotification = mapNotification(sanityFormProps.serverErrorNotification)
  }
  if (sanityFormProps.singleFieldErrorNotification) {
    form.fieldError = mapNotification(sanityFormProps.singleFieldErrorNotification)
  }
  if (sanityFormProps.multipleFieldsErrorNotification) {
    form.multipleFieldError = mapNotification(sanityFormProps.multipleFieldsErrorNotification)
  }

  return form
}

/**
 * Map a Sanity OrCardCarousel Object to Atomic Lib Carousel props
 * @param sanityCarouselProps
 * @returns OrCardCarouselProps Object
 */
const sanityCarouselToCarousel = (
  sanityCarouselProps: SanityOrCarousel,
  buildings: Building[]
): OrCardCarouselProps => {
  const carousel = {} as OrCardCarouselProps
  carousel.title = sanityCarouselProps.title
  if (sanityCarouselProps.carouselType === SanityOrCarouselType.Building) {
    carousel.type = CarouselType.Building
    carousel.cards = sanityCarouselProps.buildings.map((b) => sanityBuildingCardToBuildingCard(b))
  } else if (sanityCarouselProps.carouselType === SanityOrCarouselType.Product) {
    // @ts-expect-error Image Type
    carousel.cards = sanityCarouselProps.products.map((p) =>
      mapSpaceCard(
        {
          ...p,
          images: p.images?.map((image) => mapImage(image, { width: 600, height: 425 })),
        },
        { buildings }
      )
    )
  } else if (sanityCarouselProps.carouselType === SanityOrCarouselType.CustomCard) {
    carousel.type = CarouselType.CustomCard
    carousel.cards = sanityCarouselProps.customCards.map((c) => sanityMlCustomCardToMlCustomCard(c))
  }

  return carousel
}

const sanityBuildingCardToBuildingCard = ({
  building,
  page,
  buildingImages,
  address,
  spaceRates,
}: SanityBuildingCard): MlBuildingCardProps => {
  const learnMoreUrl = page?.url ? addBasePath(page.url) : '#'
  const tourUrl = getTourUrl(building)
  const buildingImage = Array.isArray(buildingImages) ? buildingImages[0] : buildingImages
  return {
    title: page?.name ?? building,
    images: [mapImage(buildingImage, { width: 600, height: 425 })],
    href: learnMoreUrl,
    address: parseBuildingAddress(address),
    rates: spaceRates?.map(({ spaceDescription, rate }) => ({ description: spaceDescription, rate })) ?? [],
    primaryLabel: 'Schedule a tour',
    primaryActionValue: tourUrl,
    secondaryLabel: 'Learn more',
    secondaryActionValue: learnMoreUrl,
  }
}

const getTourUrl = (building: string) => {
  const tourUrl = new URL(addBasePath('/contact'), baseUrl)
  tourUrl.searchParams.set('tab', 'tour')
  tourUrl.searchParams.set('building', building)

  return tourUrl.toString()
}

const parseBuildingAddress = (text: PortableTextProps['value']): PortableTextProps['value'] => {
  if (!Array.isArray(text)) {
    return text
  }

  const nodes = text[0]?.children
  if (!Array.isArray(nodes)) {
    return text
  }

  if ((nodes[0]?.text ?? '').startsWith('Address:') && nodes[1]) {
    return { _type: 'block', children: [{ ...nodes[1], text: nodes[1].text?.trim() ?? '' }] }
  }

  return text
}

/**
 * Map a Sanity MlSiteBanner to a Atomic Lib MlSiteBanner
 * @param sanityMlSiteBanner
 * @returns Atomic Lib MlSiteBanner
 */
const sanityMlSiteBannerToMlSiteBanner = (
  sanityMlSiteBanner: SanityMlSiteBanner,
  buildings: Building[]
): MlSiteBannerProps => {
  const mlSiteBanner = {} as MlSiteBannerProps

  mlSiteBanner.cta = sanityMlSiteBanner.callToAction
    ? mapLink(sanityMlSiteBanner.callToAction)
    : ({} as AtLinkButtonProps)
  mlSiteBanner.description = sanityMlSiteBanner.description || ''
  mlSiteBanner.headline = sanityMlSiteBanner.headline || ''
  mlSiteBanner.media = sanityMlSiteBanner.media?.[0]
    ? mapImage(sanityMlSiteBanner.media[0], { width: 1440, height: 960 })
    : (sanityImageToAtImage(sanityMlSiteBanner.image, { width: 1440, height: 960 }) as Media)
  if (sanityMlSiteBanner.variant === 'home-search' && sanityMlSiteBanner.mediaDesktop?.[0]) {
    mlSiteBanner.mediaDesktop = mapImage(sanityMlSiteBanner.mediaDesktop[0], { width: 1440, height: 960 })
  }
  mlSiteBanner.logo = sanityMlSiteBanner.logo ? sanityImageToAtImage(sanityMlSiteBanner.logo) : undefined
  mlSiteBanner.name = sanityMlSiteBanner.name || ''
  mlSiteBanner.variant = sanityMlSiteBanner.variant || 'heading-cta'
  const orientation = sanityMlSiteBanner.orientation
  mlSiteBanner.orientation =
    orientation === 'normal' ? 'image-left' : orientation === 'reverse' ? 'image-right' : 'image-left'
  mlSiteBanner.alignment = sanityMlSiteBanner.alignment || 'left'

  // Build locationOptions for home-search variant
  if (mlSiteBanner.variant === 'home-search') {
    const locationOptions: DropdownOption[] = []
    buildings.forEach(({ active, city }) => {
      if (active && !locationOptions.some((o) => o.value === city.key)) {
        locationOptions.push({ value: city.key, label: city.name, id: city.key })
      }
    })
    mlSiteBanner.locationOptions = locationOptions
    if (sanityMlSiteBanner.solutions?.length) {
      mlSiteBanner.solutions = sanityMlSiteBanner.solutions
    }
    if (sanityMlSiteBanner.defaultSolutionKey) {
      mlSiteBanner.defaultSolutionKey = sanityMlSiteBanner.defaultSolutionKey
    }
  }

  return mlSiteBanner
}

/**
 * Map a Sanity Image Asset to an object compatible with Atomic Lib AtImageProps and DefaultImageProps
 * @param sanityMedia
 * @param options Optional image URL builder options for responsive sizing
 * @returns Atomic Lib AtImageProps and DefaultImageProps
 */
const sanityImageToAtImage = (
  sanityImage: SanityAtImage & { type?: string },
  options?: Partial<ImageUrlBuilderOptionsWithAliases>
): AtImageProps & DefaultImageProps => {
  const atImage = {} as AtImageProps & DefaultImageProps & { type?: string }
  const image = mapImage(sanityImage, options)

  atImage.alt = image?.alt || ''
  atImage.src = image?.src || ''
  atImage.title = image?.caption || ''
  atImage.caption = image?.caption || ''
  atImage.type = image?.type || ''
  atImage.width = image?.width
  atImage.height = image?.height

  return atImage
}

/**
 * Map a Sanity OrBaseContent to Atomic Lib OrBaseContent
 * @param sanityOrBaseContent
 * @returns Atomic Lib OrBaseContent
 */
export const mapSanityOrBaseContentToOrBaseContent = (sanityOrBaseContent: SanityOrBaseContent): OrBaseContentProps => {
  let orBaseContentProps = {} as OrBaseContentProps
  const layout = sanityOrBaseContent.layout

  const abConfig = sanityOrBaseContent.videoABTestingConfig
  const resolvedMedia: Media[] | null = abConfig
    ? (() => {
        const picked = getSelectedVideoVariant(abConfig)
        if (!picked) return sanityOrBaseContent.media
        const rest = sanityOrBaseContent.media?.filter((m) => m.type !== 'video') ?? []
        return [picked as Media, ...rest]
      })()
    : sanityOrBaseContent.media
  const isContentBlockA = Boolean(layout === 'descriptions')
  const isContentBlockB = Boolean(layout === 'longText')
  const isContentBlockC = Boolean(layout === 'finalAddendum')
  const isContentBlockD = Boolean(layout === 'paragraphBody-d')
  const isContentBlockE = Boolean(layout === 'featureList')
  const isContentBlockF = Boolean(layout === 'paragraphBody')
  const isContentBlockG = Boolean(layout === 'fetureListWithColumns')

  if (isContentBlockA) {
    const contentBlockA = {} as OrContentBlockAProps
    contentBlockA.contents = (sanityOrBaseContent.list?.map((element) => ({
      ...element,
      link: element.link ? mapLink(element.link) : ({} as AtLinkButtonProps),
    })) ?? []) as contentItems[]
    contentBlockA.media = resolvedMedia
      ? resolvedMedia.map((m) => mapImage(m, { width: 1233, height: 925 }))
      : (sanityOrBaseContent.images?.map((img) =>
          sanityImageToAtImage(img, { width: 1233, height: 925 })
        ) as Media[]) || []

    contentBlockA.title = sanityOrBaseContent.sectionTitle || ''
    contentBlockA.orientation = sanityOrBaseContent.orientation || 'normal'
    contentBlockA.variant = sanityOrBaseContent.splitContent || 'full'
    contentBlockA.background = sanityOrBaseContent.background || 'gray'

    orBaseContentProps = { layout: 'contentBlockA', fields: contentBlockA as OrBaseContentProps['fields'] }
  }

  if (isContentBlockB) {
    const contentBlockB = {} as OrContentBlockBProps
    const { images = [] } = sanityOrBaseContent
    const media = resolvedMedia ?? []
    contentBlockB.title = sanityOrBaseContent.sectionTitle || ''
    contentBlockB.paragraphOne = sanityOrBaseContent.sectionDescription || ''
    contentBlockB.content = (sanityOrBaseContent.sectionBody as PortableTextProps['value']) || null
    contentBlockB.footer = sanityOrBaseContent.sectionFooter || ''
    contentBlockB.mediaOne =
      media && media?.length > 0
        ? mapImage(media[0])
        : images && images?.length > 0
        ? (sanityImageToAtImage(images[0]) as Media)
        : null
    contentBlockB.mediaTwo =
      media && media?.length > 1
        ? mapImage(media[1])
        : images && images?.length > 1
        ? (sanityImageToAtImage(images[1]) as Media)
        : null
    orBaseContentProps = { layout: 'contentBlockB', fields: contentBlockB as OrBaseContentProps['fields'] }
  }

  if (isContentBlockC) {
    const contentBlockC = {} as OrContentBlockCProps
    const { images = [] } = sanityOrBaseContent
    const media = resolvedMedia ?? []

    contentBlockC.mainText = sanityOrBaseContent.outline || ''
    contentBlockC.title = sanityOrBaseContent.sectionTitle || ''
    contentBlockC.subTitle = sanityOrBaseContent.sectionSubtitle || ''
    contentBlockC.body = sanityOrBaseContent.sectionDescription || ''
    contentBlockC.signature = sanityOrBaseContent.handwrittenDetail || ''
    contentBlockC.media =
      media && media?.length > 0
        ? mapImage(media[0])
        : images && images?.length > 0
        ? (sanityImageToAtImage(images[0]) as Media)
        : null
    contentBlockC.mainTextTwo = sanityOrBaseContent.outline2 || ''
    contentBlockC.titleTwo = sanityOrBaseContent.sectionTitle2 || ''
    contentBlockC.bodyTwo = sanityOrBaseContent.sectionDescription2 || ''
    contentBlockC.mediaTwo =
      media && media?.length > 1
        ? mapImage(media[1])
        : images && images?.length > 1
        ? (sanityImageToAtImage(images[1]) as Media)
        : null
    contentBlockC.orientation = sanityOrBaseContent.orientation || 'normal'
    contentBlockC.variant = sanityOrBaseContent.splitContent || 'full'
    const button = mapButton(sanityOrBaseContent?.button || ({} as SanityAtButton))
    contentBlockC.button = button
    contentBlockC.buttonText = button.title || ''

    orBaseContentProps = { layout: 'contentBlockC', fields: contentBlockC as OrBaseContentProps['fields'] }
  }

  if (isContentBlockD) {
    const contentBlockD = {} as OrContentBlockDProps

    contentBlockD.title = sanityOrBaseContent.sectionTitle || ''
    contentBlockD.media = resolvedMedia
      ? resolvedMedia.map((m) => mapImage(m))
      : (sanityOrBaseContent.images?.map((img) => sanityImageToAtImage(img)) as Media[]) || []
    contentBlockD.content = sanityOrBaseContent.sectionDescription || ''
    if (sanityOrBaseContent?.ctaBar) {
      contentBlockD.ctaBar = sanityCtaBarToMlCtaBar(sanityOrBaseContent.ctaBar)
    }

    orBaseContentProps = { layout: 'contentBlockD', fields: contentBlockD as OrBaseContentProps['fields'] }
  }

  if (isContentBlockE) {
    const contentBlockE = {} as OrContentBlockEProps

    contentBlockE.title = sanityOrBaseContent.sectionTitle || ''
    contentBlockE.subTitle = sanityOrBaseContent.sectionDescription || ''
    contentBlockE.informative = (sanityOrBaseContent.list?.map((element) => ({
      title: element.title || '',
      label: element.subtitle || '',
      icon: element?.icon?.src || '',
    })) ?? []) as AtIconInformativeProps[]
    contentBlockE.media = resolvedMedia
      ? resolvedMedia.map((m) => mapImage(m))
      : (sanityOrBaseContent.images?.map((img) => sanityImageToAtImage(img)) as Media[]) || []

    if (sanityOrBaseContent?.button) {
      const button = mapButton(sanityOrBaseContent.button)
      contentBlockE.button = button
      contentBlockE.buttonText = button.title || ''
    }

    orBaseContentProps = { layout: 'contentBlockE', fields: contentBlockE as OrBaseContentProps['fields'] }
  }

  if (isContentBlockF) {
    const contentBlockF = {} as OrContentBlockFProps
    const { images = [] } = sanityOrBaseContent
    const media = resolvedMedia ?? []

    contentBlockF.body = (sanityOrBaseContent.sectionBody as PortableTextProps['value']) || null
    contentBlockF.mediaOne =
      media && media?.length > 0
        ? mapImage(media[0])
        : images && images?.length > 0
        ? (sanityImageToAtImage(images[0]) as Media)
        : null
    contentBlockF.mediaTwo =
      media && media?.length > 1
        ? mapImage(media[1])
        : images && images?.length > 1
        ? (sanityImageToAtImage(images[1]) as Media)
        : null
    contentBlockF.orientation = sanityOrBaseContent.orientation || 'normal'
    contentBlockF.title = sanityOrBaseContent.sectionTitle || ''
    contentBlockF.orientation = sanityOrBaseContent.orientation || 'normal'

    orBaseContentProps = { layout: 'contentBlockF', fields: contentBlockF as OrBaseContentProps['fields'] }
  }

  if (isContentBlockG) {
    const contentBlockG = {} as OrContentBlockGProps

    contentBlockG.title = sanityOrBaseContent.sectionTitle || ''
    contentBlockG.subTitle = sanityOrBaseContent.sectionDescription || ''
    contentBlockG.informative = (sanityOrBaseContent.list?.map((element) => ({
      title: element.title || '',
      label: element.subtitle || '',
      icon: element?.icon?.src || '',
    })) ?? []) as AtIconInformativeProps[]
    contentBlockG.media = resolvedMedia
      ? resolvedMedia.map((m) => mapImage(m))
      : (sanityOrBaseContent.images?.map((img) => sanityImageToAtImage(img)) as Media[]) || []
    orBaseContentProps = { layout: 'contentBlockG', fields: contentBlockG as OrBaseContentProps['fields'] }
  }

  return orBaseContentProps
}

/**
 * Map a Sanity OrMap to Atomic Lib OrMapProps
 * @param sanityOrMap
 * @returns Atomic Lib OrMapProps
 */
const sanityOrMapToOrMap = (sanityOrMap: SanityOrMap): OrMapProps => {
  const orMap = {} as OrMapTileProps

  orMap.apiKey = googleMapsApiKey || ''

  orMap.title = sanityOrMap.title
  orMap.description = sanityOrMap.description

  orMap.markers = [] as OrMapProps['markers']
  for (const location of sanityOrMap.Locations) {
    const { building } = location

    orMap.markers.push({
      position: location.location,
      address: location.address,
      cardPopup: building
        ? {
            image:
              building.buildingImages && building.buildingImages?.length > 0
                ? sanityImageToAtImage(building.buildingImages[0])
                : ({} as Image),
            title: building.page?.name ?? '',
            address:
              building.address && building.address?.addressInfo
                ? parseBuildingAddress(building.address?.addressInfo)
                : ({} as PortableTextProps['value']),
            button: {
              title: 'Learn more',
              openUrl: building.page?.url ? addBasePath(building.page.url) : '#',
            },
          }
        : null,
    })

    if (location.details) {
      orMap.details = location.details.items.map((detailItem, idx) => ({
        title: detailItem ? detailItem.title || '' : '',
        icon: detailItem ? detailItem?.icon?.src || '' : '',
      }))
    }
  }

  return orMap
}

/**
 * Map a Sanity MlCustomCard to Atomic Lib MlCustomCardProps
 * @param sanityCustomCard
 * @returns Atomic Lib MlCustomCardProps
 */
const sanityMlCustomCardToMlCustomCard = ({
  name,
  title,
  description,
  image,
  ctaBar,
}: SanityCustomCard): MlCustomCardProps => {
  return {
    title,
    description,
    image: sanityImageToAtImage(image, { width: 600, height: 425 }),
    ...(ctaBar ? { ctaBar: sanityCtaBarToMlCtaBar(ctaBar) } : {}),
  }
}
